/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const VERIFY_AUTHORIZATION_DISCRIMINATOR = new Uint8Array([
  43, 9, 131, 59, 74, 118, 113, 170,
]);

export function getVerifyAuthorizationDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    VERIFY_AUTHORIZATION_DISCRIMINATOR,
  );
}

export type VerifyAuthorizationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthorization extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthorization extends string
        ? ReadonlyAccount<TAccountAuthorization>
        : TAccountAuthorization,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type VerifyAuthorizationInstructionData = {
  discriminator: ReadonlyUint8Array;
  nonce: Option<string>;
};

export type VerifyAuthorizationInstructionDataArgs = {
  nonce: OptionOrNullable<string>;
};

export function getVerifyAuthorizationInstructionDataEncoder(): Encoder<VerifyAuthorizationInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "nonce",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: VERIFY_AUTHORIZATION_DISCRIMINATOR,
    }),
  );
}

export function getVerifyAuthorizationInstructionDataDecoder(): Decoder<VerifyAuthorizationInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    [
      "nonce",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getVerifyAuthorizationInstructionDataCodec(): Codec<
  VerifyAuthorizationInstructionDataArgs,
  VerifyAuthorizationInstructionData
> {
  return combineCodec(
    getVerifyAuthorizationInstructionDataEncoder(),
    getVerifyAuthorizationInstructionDataDecoder(),
  );
}

export type VerifyAuthorizationInput<
  TAccountAgent extends string = string,
  TAccountAuthorization extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authorization account (PDA) */
  authorization: Address<TAccountAuthorization>;
  /** Clock for timestamp validation */
  clock?: Address<TAccountClock>;
  nonce: VerifyAuthorizationInstructionDataArgs["nonce"];
};

export function getVerifyAuthorizationInstruction<
  TAccountAgent extends string,
  TAccountAuthorization extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: VerifyAuthorizationInput<
    TAccountAgent,
    TAccountAuthorization,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): VerifyAuthorizationInstruction<
  TProgramAddress,
  TAccountAgent,
  TAccountAuthorization,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agent: { value: input.agent ?? null, isWritable: false },
    authorization: { value: input.authorization ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authorization),
      getAccountMeta(accounts.clock),
    ],
    data: getVerifyAuthorizationInstructionDataEncoder().encode(
      args as VerifyAuthorizationInstructionDataArgs,
    ),
    programAddress,
  } as VerifyAuthorizationInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountAuthorization,
    TAccountClock
  >);
}

export type ParsedVerifyAuthorizationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Agent account */
    agent: TAccountMetas[0];
    /** Authorization account (PDA) */
    authorization: TAccountMetas[1];
    /** Clock for timestamp validation */
    clock: TAccountMetas[2];
  };
  data: VerifyAuthorizationInstructionData;
};

export function parseVerifyAuthorizationInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedVerifyAuthorizationInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agent: getNextAccount(),
      authorization: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getVerifyAuthorizationInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
